import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { lotteryApi } from '../../api/lottery.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, Field, SortableTh, SearchInput } from '../../components/ui.js';
import { LotteryGradeBadge } from '../../components/LotteryGradeBadge.js';
import { useUrlUpdate, useUrlSearch } from '../../lib/hooks.js';
import { text, money, num, datetime } from '../../lib/format.js';
import { titleColorStyle } from '../../lib/titleColor.js';

/**
 * 원본: lottery/order/lottery_order_list.php + lottery_load_more_data.php
 *
 * 원본은 무한 스크롤로 AJAX 조각 HTML 을 이어붙였는데, 여기서는 일반 페이지네이션으로
 * 바꿨다. 관리자 화면에서 "3페이지 12번째 줄"을 다시 찾아가야 하는 일이 잦고,
 * 무한 스크롤은 그게 안 되기 때문이다. 필터는 URL 에 남아 새로고침/공유가 된다.
 *
 * 체크박스 · 엑셀 다운로드 · 오른쪽 상세 패널은 랜덤박스 주문 목록
 * (randombox/OrdersPage.tsx)과 같은 방식이다. 원본의 EXCEL 버튼
 * (lottery_excel_output.php)은 "지금 검색조건에 걸린 전부"를 뽑았는데,
 * 여기서는 체크한 주문만 뽑는다.
 *
 * 랜덤박스(kuji)를 하나 골라 좁혀 보는 중에는 페이징하지 않고 한 페이지에 다
 * 보여준다 - 특정 회차 주문을 훑어볼 때 페이지를 넘기며 찾는 게 오히려 불편하다는
 * 요청이었다. 실제 회차별 최대 주문 수(수백 건)가 500 안에 넉넉히 들어와서 그
 * 값을 상한으로 잡았다 - 아주 드물게 한 회차가 500건을 넘으면 그때만 다시
 * 페이지가 나뉜다. 서버 lottery.schemas.ts 의 LOTTERY_ORDER_MAX_PER_PAGE 와
 * 반드시 같은 값이어야 한다(다르면 "perPage 는 최대 N 까지만 허용됩니다" 오류가
 * 난다 - orders.routes.ts 의 normalizePage 호출부 주석 참고).
 *
 * 카테고리 → 랜덤박스는 부모-자식 필터다. 카테고리를 고르면 productOptions 를
 * 그 카테고리로 다시 조회해 콤보박스를 좁히고, 이전에 고른 랜덤박스가 새 목록에
 * 없을 수 있으니 kuji 를 함께 지운다. 필터 순서는 카테고리를 맨 앞에, 검색을
 * 가장 오른쪽 끝에 두라는 요청에 맞췄다.
 *
 * 목록의 "수취인"을 누르면 randombox 주문 목록의 회원 클릭과 같은 방식으로,
 * 그 사람의 くじ 당첨 이력만 모달로 보여준다(F고객별 당첨리스트 리포트와 같은
 * 집계를 한 명으로 좁힌 것 - server orders.routes.ts 의 /customers/:memIdx/prizes
 * 참고). 전체 회원을 검색·페이징하려면 그 리포트 화면(F고객별주문)으로 가야 한다.
 */
/**
 * 랜덤박스를 안 골랐을 때의 안내. 서버(delivery.schemas.ts 의
 * KUJI_REQUIRED_MESSAGE)도 같은 문구를 쓴다 - 버튼에서 막히든 주소를 직접
 * 두드려 서버에서 막히든 같은 말이 나와야 한다.
 */
const KUJI_REQUIRED_MESSAGE =
  '랜덤박스를 선택해주세요. 선택하지 않으면 올바른 데이터가 출력되지 않습니다.';

export function OrdersPage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customerModal, setCustomerModal] = useState<{ idx: number; name: string } | null>(null);
  const [cjOpen, setCjOpen] = useState(false);
  const [bizhostOpen, setBizhostOpen] = useState(false);
  // 배송처리용·비즈호스트 등록용이 함께 쓰는 "랜덤박스를 고르라"는 오류 - 문구가
  // 같아서 한 곳에만 띄운다 (따로 두면 두 버튼을 눌렀을 때 같은 문구가 두 번 뜬다).
  const [kujiError, setKujiError] = useState<string | null>(null);

  const page = Number(params.get('page') ?? '1');
  const sort = params.get('sort') ?? 'orderDate';
  const dir = (params.get('dir') ?? 'desc') as 'asc' | 'desc';
  const categoryCode = params.get('categoryCode') ?? '';
  const kuji = params.get('kuji') ?? '';

  // 랜덤박스를 골랐으면 페이징을 끄고 한 페이지에 다 담는다(위 주석 참고).
  // 랜덤박스 필터를 고르거나 지우면 useUrlUpdate 가 기본으로 page 를 지워
  // 1페이지로 되돌리므로, 두 모드를 오갈 때 엉뚱한 페이지 번호가 남지 않는다.
  const perPage = kuji === '' ? 30 : 500;

  const update = useUrlUpdate(params, setParams);
  const [search, setSearch] = useUrlSearch(params, update);
  const debouncedSearch = params.get('q') ?? '';

  const categories = useQuery({
    queryKey: ['lottery', 'categories', 'all'],
    queryFn: () => lotteryApi.categories({ page: 1, perPage: 200 }),
    staleTime: 10 * 60 * 1000,
  });

  // 카테고리를 고르면 그 카테고리에 속한 랜덤박스만 옵션으로 받는다 - queryKey 에
  // categoryCode 를 넣어야 카테고리를 바꿀 때 다시 조회한다.
  const productOptions = useQuery({
    queryKey: ['lottery', 'productOptions', categoryCode],
    queryFn: () => lotteryApi.productOptions(categoryCode || undefined),
    staleTime: 10 * 60 * 1000,
  });

  const query = useQuery({
    queryKey: ['lottery', 'orders', { page, perPage, sort, dir, q: debouncedSearch, kuji }],
    queryFn: () =>
      lotteryApi.orders({
        page,
        perPage,
        sort,
        dir,
        q: debouncedSearch || undefined,
        kuji: kuji === '' ? undefined : Number(kuji),
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.items ?? [];
  /** 엑셀은 주문번호로 뽑으므로(원본 쿼리가 order_no 기준) 번호가 없는 행은 못 고른다. */
  const selectable = rows.filter((o): o is typeof o & { orderNo: string } => Boolean(o.orderNo));
  const allChecked = selectable.length > 0 && selectable.every((o) => selected.has(o.orderNo));

  function toggleOne(orderNo: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orderNo)) next.delete(orderNo);
      else next.add(orderNo);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(selectable.map((o) => o.orderNo)));
  }

  return (
    <section>
      <header className="page-head">
        <h1 className="page-title">주문 관리</h1>
        {query.isFetching && <span className="page-head__busy">갱신 중…</span>}
      </header>

      <div className="filters">
        <Field label="카테고리">
          <select
            className="input"
            value={categoryCode}
            onChange={(e) => update({ categoryCode: e.target.value, kuji: undefined })}
          >
            <option value="">전체</option>
            {categories.data?.items.map((c) => (
              <option key={c.companyCode ?? c.seq} value={c.companyCode ?? ''}>
                {text(c.companyName)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="랜덤박스">
          {/* 카테고리를 고르면 그 카테고리 소속 랜덤박스만 뜬다(위 productOptions 참고). */}
          <select className="input" value={kuji} onChange={(e) => update({ kuji: e.target.value })}>
            <option value="">전체</option>
            {productOptions.data?.map((o) => (
              <option key={o.lotteryId ?? o.label} value={o.lotteryId ?? ''}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <button
          type="button"
          className="btn btn--ghost filters__reset"
          onClick={() => {
            setSearch('');
            setParams(new URLSearchParams(), { replace: true });
          }}
        >
          필터 초기화
        </button>

        {/* 가장 오른쪽 끝에 둔다 - filters__reset 의 margin-left:auto 뒤를 그대로 따라간다. */}
        <Field label="검색 (주문번호 · 수취인명 · 연락처)">
          <SearchInput
            value={search}
            placeholder="예: 250525-001"
            onChange={setSearch}
          />
        </Field>
      </div>

      <div className="toolbar">
        {/* <a> 는 disabled 가 없어서, 고른 게 없을 때는 비활성 버튼으로 바꿔 그린다. */}
        {selected.size === 0 ? (
          <button type="button" className="btn btn--primary btn--sm" disabled>
            엑셀 다운로드 (0)
          </button>
        ) : (
          <a className="btn btn--primary btn--sm" href={lotteryApi.excelExportUrl([...selected])}>
            엑셀 다운로드 ({selected.size})
          </a>
        )}
        {/*
          배송처리용·송장입력은 원본 목록 화면(lottery_order_list_kr.php)의 버튼
          두 개다. 비즈호스트 등록용은 랜덤박스 주문의 비즈호스트 등록용과 같은
          양식의 엑셀이다. 위의 '엑셀 다운로드'(くじ別集計)와 달리 셋 다 체크박스로
          고른 주문이 아니라 **지금 걸린 조건 전체**가 대상이라 선택 개수를 안 붙인다.
        */}
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            // 랜덤박스를 안 고르면 회차가 뒤섞인 배송 파일이 나간다. 원본은
            // 모달 안에 안내문만 띄우고 다운로드는 그대로 열어뒀는데, 여기서는
            // 아예 못 받게 막고 오류를 보여준다 (서버도 kuji 를 필수로 받는다).
            if (kuji === '') {
              setKujiError(KUJI_REQUIRED_MESSAGE);
              setCjOpen(false);
              return;
            }
            setKujiError(null);
            setCjOpen(true);
          }}
        >
          CJ배송처리용
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            // 배송처리용과 같은 이유로 랜덤박스를 안 고르면 막는다.
            if (kuji === '') {
              setKujiError(KUJI_REQUIRED_MESSAGE);
              setBizhostOpen(false);
              return;
            }
            setKujiError(null);
            setBizhostOpen(true);
          }}
        >
          비즈호스트 등록용
        </button>
        <Link
          className="btn btn--ghost btn--sm"
          to={`/lottery/tracking${kuji ? `?kuji=${kuji}` : ''}`}
        >
          송장입력
        </Link>
        {selected.size > 0 && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelected(new Set())}>
            선택 해제
          </button>
        )}
      </div>

      {kujiError && <ErrorBox error={new Error(kujiError)} />}

      {cjOpen && (
        <div className="modal-overlay" onClick={() => setCjOpen(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              onClick={() => setCjOpen(false)}
              aria-label="닫기"
            >
              ×
            </button>
            <h2 className="modal-title">배송처리용 · CJ대한통운 업로드</h2>
            {/*
              랜덤박스가 없으면 여기까지 못 온다(위 버튼에서 막는다).
              주문취소 건은 서버 쿼리에서 빠진다 - 원본 lottery_excel_output_delivery.php:36
              의 `lo.order_status != '주문취소'` 를 그대로 쓴다.
            */}
            <p className="hint">
              지금 걸린 조건(랜덤박스 · 검색어)에 해당하는 주문 전체가 대상입니다.
              같은 사람의 주문은 배송 한 줄로 묶이고, <strong>주문취소 건은 빠집니다.</strong>
            </p>
            <div className="form-grid">
              <a
                className="btn btn--primary btn--block"
                href={lotteryApi.cjExportUrl({
                  kuji: kuji === '' ? '' : Number(kuji),
                  q: debouncedSearch || undefined,
                })}
              >
                검색된 데이터 다운로드
              </a>
            </div>
          </div>
        </div>
      )}

      {bizhostOpen && (
        <div className="modal-overlay" onClick={() => setBizhostOpen(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              onClick={() => setBizhostOpen(false)}
              aria-label="닫기"
            >
              ×
            </button>
            <h2 className="modal-title">비즈호스트 등록용</h2>
            {/*
              랜덤박스가 없으면 여기까지 못 온다(위 버튼에서 막는다).
              대상 상태는 서버 쿼리가 정한다 - 랜덤박스 주문의 비즈호스트 등록용과 같이
              미배송만, 배송취소한 경품 줄은 뺀다 (lottery/delivery.routes.ts 참고).
            */}
            <p className="hint">
              지금 걸린 조건(랜덤박스 · 검색어)에 해당하는 <strong>미배송</strong> 주문 전체가
              대상입니다. 랜덤박스 주문의 비즈호스트 등록용과 같은 양식의 엑셀로 내려받습니다.
            </p>
            <div className="form-grid">
              <a
                className="btn btn--primary btn--block"
                href={lotteryApi.bizhostExportUrl({
                  kuji: kuji === '' ? '' : Number(kuji),
                  q: debouncedSearch || undefined,
                })}
              >
                엑셀 다운로드
              </a>
            </div>
          </div>
        </div>
      )}

      {query.isPending ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorBox error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyBox />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      disabled={selectable.length === 0}
                      onChange={toggleAll}
                      aria-label="전체 선택"
                    />
                  </th>
                  <th>#</th>
                  <SortableTh label="주문번호" sortKey="orderNo" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <SortableTh label="주문일" sortKey="orderDate" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <th>랜덤박스</th>
                  <SortableTh label="수취인" sortKey="memberName" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <SortableTh label="티켓" sortKey="ticket" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <SortableTh label="금액" sortKey="totalAmount" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <SortableTh label="상태" sortKey="orderStatus" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <th>상세보기</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((order, i) => (
                  <tr key={order.orderNo ?? i}>
                    <td>
                      {order.orderNo && (
                        <input
                          type="checkbox"
                          checked={selected.has(order.orderNo)}
                          onChange={() => toggleOne(order.orderNo!)}
                          aria-label={`${order.orderNo} 선택`}
                        />
                      )}
                    </td>
                    <td className="td-muted">{(page - 1) * query.data.perPage + i + 1}</td>
                    <td>
                      {order.orderNo ? (
                        // state.background 를 넘기면 App.tsx 가 목록 위에 상세를
                        // 겹쳐 띄운다 - 목록은 그대로 살아있다.
                        <Link
                          className="link"
                          to={`/lottery/orders/${encodeURIComponent(order.orderNo)}`}
                          state={{ background: location }}
                        >
                          {order.orderNo}
                        </Link>
                      ) : (
                        '-'
                      )}
                      {order.memo && (
                        <span className="badge badge--memo" title={order.memo}>
                          메모
                        </span>
                      )}
                    </td>
                    <td className="td-muted">{datetime(order.orderDate)}</td>
                    <td>
                      <span className="product-cell">
                        {order.product.thumbnail && (
                          <img className="thumb" src={order.product.thumbnail} alt="" loading="lazy" />
                        )}
                        <ProductTitle name={order.product.name} color={order.product.color} />
                        {order.isOnahole && <span className="tag">오나홀</span>}
                      </span>
                    </td>
                    <td>
                      {/* lottery_member.del_name - 회원명이 아니라 수취인명이다. */}
                      {order.member.idx !== null ? (
                        <button
                          type="button"
                          className="link"
                          onClick={() =>
                            setCustomerModal({ idx: order.member.idx!, name: order.member.name ?? '' })
                          }
                        >
                          {text(order.member.name)}
                        </button>
                      ) : (
                        text(order.member.name)
                      )}
                      {order.member.idx !== null && (
                        <span className="td-sub">#{order.member.idx}</span>
                      )}
                    </td>
                    <td className="td-right">{num(order.ticket)}</td>
                    <td className="td-right">{money(order.totalAmount)}</td>
                    <td>
                      <span
                        className={`badge${order.orderStatus === '주문취소' ? ' badge--danger' : ''}`}
                      >
                        {text(order.orderStatus)}
                      </span>
                    </td>
                    <td>
                      {/* 주문번호 클릭과 같은 상세 패널을 연다. */}
                      {order.orderNo ? (
                        <Link
                          className="btn btn--ghost btn--sm"
                          to={`/lottery/orders/${encodeURIComponent(order.orderNo)}`}
                          state={{ background: location }}
                        >
                          상세보기
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            total={query.data.total}
            onChange={(p) => update({ page: p }, false)}
          />
        </>
      )}

      {customerModal && (
        <CustomerPrizesModal member={customerModal} onClose={() => setCustomerModal(null)} />
      )}
    </section>
  );
}

/**
 * 랜덤박스 제목. 원본 lottery_load_more_data_kr.php 처럼 랜덤박스마다 설정한
 * order_color 로 칠한다(값이 없거나 잘못됐으면 기본 글자색).
 */
function ProductTitle({ name, color }: { name: string | null; color: string | null }) {
  const style = titleColorStyle(color);
  return (
    <span className={style ? 'title-color' : undefined} style={style}>
      {text(name)}
    </span>
  );
}

/**
 * 원본: lottery/order/lottery_order_uu_list.php (F고객별 당첨리스트 리포트와 같은
 * 집계를 한 명으로 좁힌 것). randombox 주문 목록의 CustomerPrizesModal 과 같은
 * 구조이지만, 등급이 A~E·L 이라 randombox 의 CustomerGradeBadge(1~7·L) 대신
 * LotteryGradeBadge 를 쓴다.
 */
function CustomerPrizesModal({
  member,
  onClose,
}: {
  member: { idx: number; name: string };
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: ['lottery', 'customerPrizes', member.idx],
    queryFn: () => lotteryApi.customerPrizes(member.idx),
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">
          고객별 당첨 리스트
          <span className="page-title__sub">
            {member.name}
            {` (#${member.idx})`}
          </span>
        </h2>

        {query.isPending ? (
          <Spinner />
        ) : query.isError ? (
          <ErrorBox error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <EmptyBox>당첨 이력이 없습니다.</EmptyBox>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>랜덤박스명</th>
                  <th>등급</th>
                  <th>상품명</th>
                  <th style={{ textAlign: 'right' }}>티켓장수</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((it, i) => (
                  <tr key={it.prizeId ?? i}>
                    <td>{text(it.productName)}</td>
                    <td><LotteryGradeBadge grade={it.grade} /></td>
                    <td>{text(it.prizeTitle)}</td>
                    <td className="td-right">{num(it.ticketCount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="row--total">
                  <td colSpan={3} style={{ textAlign: 'right' }}>합계</td>
                  <td className="td-right">{num(query.data.totalTickets)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
